import React, { useEffect, useState } from "react";
import { getTeam, inviteMember, revokeInvite, removeMember, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Input } from "../components/ui/input";
import { Users, ChevronRight, UserPlus, Copy, Trash2, Mail, ShieldCheck, Loader2 } from "lucide-react";

const inviteLink = (token) => `${window.location.origin}/invite?token=${token}`;

export default function Team() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [inviting, setInviting] = useState(false);

  const load = () => getTeam().then(setData).catch(() => setData({ members: [], invites: [], is_owner: false }));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setInviting(true);
    try {
      const res = await inviteMember(email.trim());
      await navigator.clipboard?.writeText(inviteLink(res.token)).catch(() => {});
      toast.success(res.emailed ? `Invite emailed to ${res.email} — link also copied` : "Invite created — link copied to clipboard");
      setEmail("");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Invite failed");
    }
    setInviting(false);
  };

  const copyLink = async (token) => {
    try { await navigator.clipboard.writeText(inviteLink(token)); toast.success("Invite link copied"); }
    catch { toast.error("Copy failed"); }
  };
  const revoke = async (token) => { try { await revokeInvite(token); toast.info("Invite revoked"); load(); } catch { toast.error("Failed"); } };
  const remove = async (id) => { try { await removeMember(id); toast.info("Member removed"); load(); } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); } };

  const isOwner = data?.is_owner;

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <span className="text-zinc-600">~/</span> workspace <ChevronRight className="h-3 w-3" /> team
        </span>
      </header>

      <div className="p-5 sm:p-8 max-w-3xl" data-testid="team-page">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold">
          <Users className="h-4 w-4" /> Team &amp; Seats
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white tracking-tight mt-2">Your Workspace Team</h1>
        <p className="text-zinc-400 mt-3">Invite colleagues into this private workspace. Everyone shares the same estate data, runbooks and trackers.</p>

        {/* Invite */}
        {isOwner && (
          <form onSubmit={submit} className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 p-5" data-testid="invite-card">
            <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-blue-400 font-semibold">
              <UserPlus className="h-4 w-4" /> Invite a colleague
            </div>
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <Input data-testid="invite-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="colleague@company.com" className="h-10 bg-zinc-950 border-zinc-800 text-sm flex-1" />
              <button type="submit" disabled={inviting} data-testid="invite-submit-btn"
                className="flex items-center justify-center gap-2 text-[13px] font-semibold px-4 h-10 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white transition-colors">
                {inviting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />} Send invite
              </button>
            </div>
            <p className="text-[11px] text-zinc-500 mt-2">We generate a private link — copy it and share with your colleague. They set a password on signup.</p>
          </form>
        )}

        {/* Members */}
        <div className="mt-6">
          <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">Members</div>
          <div className="rounded-lg border border-zinc-800 divide-y divide-zinc-800 overflow-hidden">
            {(data?.members || []).map((m) => (
              <div key={m.id} data-testid={`member-row-${m.id}`} className="flex items-center gap-3 px-4 py-3 bg-zinc-900/40">
                <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white text-sm font-semibold">
                  {(m.name || m.email).trim().charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-white truncate">{m.name || m.email}</div>
                  <div className="text-[11px] text-zinc-500 truncate">{m.email}</div>
                </div>
                <span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full border ${m.role === "owner" ? "bg-emerald-600/15 text-emerald-400 border-emerald-600/30" : "bg-zinc-700/30 text-zinc-300 border-zinc-700"}`}>
                  {m.role === "owner" ? "Owner" : "Member"}
                </span>
                {isOwner && m.role !== "owner" && (
                  <button onClick={() => remove(m.id)} data-testid={`remove-member-${m.id}`} className="text-zinc-500 hover:text-red-400" title="Remove member">
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            ))}
            {!data && <div className="px-4 py-6 text-zinc-500 text-sm font-mono">Loading team…</div>}
          </div>
        </div>

        {/* Pending invites */}
        {(data?.invites || []).length > 0 && (
          <div className="mt-6">
            <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold mb-2">Pending invites</div>
            <div className="rounded-lg border border-zinc-800 divide-y divide-zinc-800 overflow-hidden">
              {data.invites.map((inv) => (
                <div key={inv.token} data-testid={`invite-row-${inv.token}`} className="flex items-center gap-3 px-4 py-3 bg-zinc-900/40">
                  <Mail className="h-4 w-4 text-amber-400 shrink-0" />
                  <div className="min-w-0 flex-1 text-sm text-zinc-200 truncate">{inv.email}</div>
                  <button onClick={() => copyLink(inv.token)} data-testid={`copy-invite-${inv.token}`} className="flex items-center gap-1.5 text-[12px] px-2.5 py-1.5 rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                    <Copy className="h-3.5 w-3.5" /> Copy link
                  </button>
                  {isOwner && (
                    <button onClick={() => revoke(inv.token)} data-testid={`revoke-invite-${inv.token}`} className="text-zinc-500 hover:text-red-400" title="Revoke">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {!isOwner && data && (
          <div className="mt-6 flex items-center gap-2 text-[12px] text-zinc-500">
            <ShieldCheck className="h-4 w-4" /> Only the workspace owner can invite or remove members.
          </div>
        )}
      </div>
    </div>
  );
}
